import { useMemo, type ReactNode } from 'react';
import {
  Box,
  Flex,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Checkbox,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { useState } from 'react';
import { MdArrowDropUp, MdArrowDropDown } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import Card from '../card/Card';
import TablePagination from './TablePagination';

export interface SortState {
  id: string;
  desc: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSortChange?: (sort: SortState | null) => void;
  isLoading?: boolean;
  /**
   * Enables the leading selection checkbox column. When a predicate is given, rows for
   * which it returns false render a disabled checkbox and cannot be selected — but their
   * `onRowClick` still fires, so consumers that combine selection with row-click must
   * handle non-selectable rows themselves.
   */
  enableRowSelection?: boolean | ((row: T) => boolean);
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /**
   * Stabilise row identity across data changes (pagination/filtering).
   * When provided, TanStack keys `rowSelection` by this id instead of the
   * row index, so a selection survives the underlying `data` array changing.
   */
  getRowId?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  showCard?: boolean;
  headerContent?: ReactNode;
  'data-testid'?: string;
}

export default function DataTable<T>({
  data,
  columns: userColumns,
  totalCount,
  page = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  isLoading = false,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  onRowClick,
  emptyText,
  showCard = true,
  headerContent,
  ...rest
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headerColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const columns = useMemo(() => {
    if (!enableRowSelection) return userColumns;
    const selectColumn: ColumnDef<T, unknown> = {
      id: 'select',
      header: ({ table }) => (
        // Stop propagation so toggling selection never triggers the row's
        // onRowClick (e.g. navigate-to-detail in the standalone library).
        <Box onClick={(e) => e.stopPropagation()} display="inline-flex">
          <Checkbox
            isChecked={table.getIsAllRowsSelected()}
            isIndeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            colorScheme="brand"
          />
        </Box>
      ),
      cell: ({ row }) => (
        <Box onClick={(e) => e.stopPropagation()} display="inline-flex">
          <Checkbox
            isChecked={row.getIsSelected()}
            isIndeterminate={row.getIsSomeSelected()}
            isDisabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            colorScheme="brand"
          />
        </Box>
      ),
      size: 40,
      enableSorting: false,
    };
    return [selectColumn, ...userColumns];
  }, [userColumns, enableRowSelection]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      if (onSortChange) {
        onSortChange(
          newSorting.length > 0
            ? { id: newSorting[0].id, desc: newSorting[0].desc }
            : null,
        );
      }
    },
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    ...(getRowId ? { getRowId } : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: onSortChange ? undefined : getSortedRowModel(),
    manualSorting: !!onSortChange,
    // Adapt our row-data predicate to TanStack's Row<T> signature.
    enableRowSelection:
      typeof enableRowSelection === 'function'
        ? (row) => enableRowSelection(row.original)
        : enableRowSelection,
  });

  const totalPages = totalCount
    ? Math.ceil(totalCount / pageSize)
    : data.length > 0
      ? 1
      : 0;

  const tableContent = (
    <Box overflowX="auto" data-testid={rest['data-testid']}>
      <Table variant="simple" size="sm">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  pe="10px"
                  borderColor={borderColor}
                  cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                  onClick={header.column.getToggleSortingHandler()}
                  color={headerColor}
                  fontSize={{ sm: '10px', lg: '12px' }}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: '10px', lg: '12px' }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {header.column.getCanSort() && (
                      <Flex direction="column" ms="4px">
                        <Icon
                          as={MdArrowDropUp}
                          w="14px"
                          h="14px"
                          color={
                            header.column.getIsSorted() === 'asc'
                              ? 'brand.500'
                              : 'gray.400'
                          }
                        />
                        <Icon
                          as={MdArrowDropDown}
                          w="14px"
                          h="14px"
                          mt="-8px"
                          color={
                            header.column.getIsSorted() === 'desc'
                              ? 'brand.500'
                              : 'gray.400'
                          }
                        />
                      </Flex>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {isLoading
            ? Array.from({ length: pageSize }).map((_, i) => (
                <Tr key={`skeleton-${i}`}>
                  {columns.map((_, j) => (
                    <Td key={`skeleton-${i}-${j}`} borderColor={borderColor}>
                      <Skeleton h="16px" borderRadius="8px" />
                    </Td>
                  ))}
                </Tr>
              ))
            : table.getRowModel().rows.length === 0
              ? (
                <Tr>
                  <Td
                    colSpan={columns.length}
                    borderColor={borderColor}
                    textAlign="center"
                    py="40px"
                  >
                    <Text color="secondaryGray.500" fontSize="sm">
                      {emptyText || t('common.noData', 'No data found')}
                    </Text>
                  </Td>
                </Tr>
              )
              : table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  _hover={onRowClick ? { bg: rowHoverBg } : undefined}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} py="12px" color={textColor} fontSize="sm" fontWeight="500">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
        </Tbody>
      </Table>
      {onPageChange && totalPages > 0 && (
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </Box>
  );

  if (!showCard) return tableContent;

  return (
    <Card p="0px">
      {headerContent && (
        <Box px="25px" pt="20px">
          {headerContent}
        </Box>
      )}
      {tableContent}
    </Card>
  );
}
